import React, { useMemo } from 'react';
import { reduxForm, initialize, InjectedFormProps } from 'redux-form';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import globalStyles from 'common/global.module.scss';
import { AppState } from 'store';
import { Form, Select, Button, Card, Row, Tooltip, Spin } from 'antd';
import {
  FormField,
  required,
  defaultDateFormat,
  InputTypes,
  SkeletonHOC,
  scrollTop,
} from 'common';
import {
  initialForm,
  USER_USAGE_REPORT_FORM,
} from '../UserUsageReport.constants';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import { getUserUsageReportsByCustomer, userUsageReportClear } from '..';
import { UserCustomer } from '../models';
import { useAccount, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { getAccessToken } from 'common/helpers';

const { Option } = Select;

interface UserUsageReportFormProps {
  loading: boolean;
  customers: UserCustomer[];
  formValues: any;
  valid: boolean;
}

type Props = UserUsageReportFormProps &
  InjectedFormProps<{}, UserUsageReportFormProps>;

const UserUsageReportForm: React.FC<Props> = ({
  loading,
  customers,
  formValues,
  valid,
}: UserUsageReportFormProps) => {
  const dispatch = useDispatch();

  const { login } = useSelector((state: AppState) => ({
    login: state.login,
  }));

  const spinnerTitle = useMemo((): string => {
    if (loading) return 'Loading customers...';
    return '';
  }, [loading]);

  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || {});

  const handleSearch = () => {
    dispatch(userUsageReportClear());

    if (account && inProgress === InteractionStatus.None) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            getUserUsageReportsByCustomer(formValues, response.accessToken),
          );
      });
    }

    scrollTop();
  };

  if (login.user?.licenseflag == 'False') {
    return <Redirect to={`/pages/license`} />;
  }

  return (
    <Card size="small">
      <Spin spinning={loading && !customers} size="large" tip={spinnerTitle}>
        <Form layout="vertical">
          <Row type="flex" align="middle">
            <FormField
              className={`${globalStyles.input} ${globalStyles.date}`}
              tabIndex="100"
              label="From Date"
              name="from"
              inputType={InputTypes.DATE_PICKER}
              datepickervalue={moment(formValues?.from, defaultDateFormat)}
              validate={required}
            />
            <FormField
              className={`${globalStyles.input} ${globalStyles.date}`}
              tabIndex="200"
              label="To Date"
              name="to"
              inputType={InputTypes.DATE_PICKER}
              datepickervalue={moment(formValues?.to, defaultDateFormat)}
              validate={required}
            />
            <FormField
              tabIndex="300"
              label="Customer or Group"
              name="customer"
              inputType={InputTypes.SELECT}
              className={globalStyles.select}
              allowClear={false}
              showSearch={true}
              filterOption={(input: any, option: any) =>
                option.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option key="" value="" title="ALL">
                ALL
              </Option>
              {customers?.map((customer) => (
                <Option
                  key={customer.code}
                  value={customer.code}
                  title={customer.name}
                >
                  {customer.name}
                </Option>
              ))}
            </FormField>
          </Row>
          <Button
            className={globalStyles.button}
            onClick={() => {
              dispatch(userUsageReportClear());
              dispatch(
                initialize(USER_USAGE_REPORT_FORM, {
                  ...initialForm,
                }),
              );
            }}
            type="ghost"
          >
            Reset
          </Button>
          <Tooltip
            title={
              valid
                ? 'Press to run search...'
                : 'Please fill all the fields properly!'
            }
          >
            <Button
              icon="search"
              type="primary"
              htmlType="submit"
              disabled={loading || !valid}
              onClick={() => handleSearch()}
            >
              Search
            </Button>
          </Tooltip>
        </Form>
      </Spin>
    </Card>
  );
};

export default compose(
  SkeletonHOC({ initProp: 'customers', rows: 3 }),
  reduxForm<{}, UserUsageReportFormProps>({
    form: USER_USAGE_REPORT_FORM,
    initialValues: initialForm,
  }),
)(React.memo(UserUsageReportForm));
